import React, {
  Fragment,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Guid } from "guid-typescript";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createBrowserHistory, Action } from "history";
import {
  Box,
  Modal,
  Menu,
  Button,
  SvgIcon,
  useTheme,
  MenuItem,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactComponent as ArrowLeft } from "assets/images/arrow-sm-left.svg";
import { ReactComponent as ArrowRight } from "assets/images/arrow-sm-right.svg";

import {
  Viewer,
  Worker,
  Tooltip,
  Position,
  ScrollMode,
  PrimaryButton,
  LocalizationMap,
  PageChangeEvent,
  SpecialZoomLevel,
  DocumentLoadEvent,
} from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import PDFJSWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import * as pdfjsLib from "pdfjs-dist";

import type { ToolbarProps, ToolbarSlot } from "@react-pdf-viewer/toolbar";

import {
  MessageIcon,
  HighlightArea,
  highlightPlugin,
  RenderHighlightContentProps,
  RenderHighlightsProps,
  RenderHighlightTargetProps,
} from "@react-pdf-viewer/highlight";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";

import { useDocumentActionsStatus } from "./hooks/DocumentActionsStatus.context";
import {
  getVersionsAsync,
  updatePdfDocumentReadDateAsync,
} from "./pdfDocuments.service";
import DialogAlertMessage from "Shared.Components/DialogAlertMessage";
import { LANGUAGES, RESOURCES_KEYS } from "utils/i18n/resources";
import AcknowledgmentConfirmation from "./Acknowledgment";
import { IDocumentVersion } from "utils/DexieDBManager";
import { computeErrorMessage } from "utils/utils";
import fetchWrapper from "utils/fetchWrapper";

// Import the localization file
import fr_FR from "@react-pdf-viewer/locales/lib/fr_FR.json";
import en_US from "@react-pdf-viewer/locales/lib/en_US.json";

import {
  faBan,
  faCheck,
  faPlus,
  faXmark,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { faPenToSquare, faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { constructResponseData } from "utils/fetchWrapper/fetchWrapper";
import { useGetDocumentStatistics } from "./hooks/useGetDocumentStatistics";
import {
  scrollModePlugin,
  WrappedScrollingIcon,
  VerticalScrollingIcon,
  HorizontalScrollingIcon,
} from "@react-pdf-viewer/scroll-mode";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { useRedirectIfUnauthenticated } from "utils/azureAD/useRedirectIfUnauthenticated";

pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJSWorker;

const boxSyle = {
  backgroundColor: "transparent",
  border: 0,
  height: `100%`,
};

const closeButtonStyle = {
  fontWeight: 700,
  fontSize: "1rem",
  padding: "4px 14px",
};

const jumpBackAndForwardButtonStyle = {
  fontWeight: 700,
};
interface IPdfViewerModalProps {
  onClose: () => void;
  isViewerOpen: boolean;
  pdfFileBuffer: ArrayBuffer;
  documentVersion: IDocumentVersion;
}

interface Note {
  id: Guid;
  noteContent: string;
  quotePosition: HighlightArea[];
  quote: string;
  editing: boolean;
}

interface Statistic {
  lastReadPage: number;
  lastReadProgression: number;
}

interface NoteRequest {
  quote: string;
  quotePosition: string;
  noteContent: string;
}

interface NoteResponse {
  id: Guid;
  documentVersionId: Guid;
  quote: string;
  quotePosition: string;
  noteContent: string;
}

const history = createBrowserHistory();

const PdfViewerModal: React.FunctionComponent<IPdfViewerModalProps> = ({
  documentVersion,
  pdfFileBuffer,
  isViewerOpen,
  onClose,
}) => {
  const { dispatch, state: documentActionsState } = useDocumentActionsStatus();
  const [isOpenDialogAlert, setOpenDialogAlert] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const scrollModePluginInstance = scrollModePlugin();

  const [currentPage, setCurrentPage] = useState(0);
  const { t: translate, i18n } = useTranslation();
  const { search, pathname } = window.location;
  const isOnline = navigator.onLine;
  const { accounts } = useRedirectIfUnauthenticated();
  const navigate = useNavigate();

  const localAccountId = accounts[0]?.localAccountId;

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;

  const zoomPluginInstance = zoomPlugin();

  const { switchScrollMode } = scrollModePluginInstance;
  const { zoomTo } = zoomPluginInstance;

  const { isLoading: isGetDocumentStatisticsLoading } =
    useGetDocumentStatistics(
      {
        documentVersionId: documentVersion.id,
        localAccountId,
      },
      {
        isEnabled: isOnline,
        onSuccess: ({ responseData = { lastReadPage: 0 } }) => {
          const { lastReadPage } = responseData;

          setCurrentPage(lastReadPage);
          jumpToPage(lastReadPage);
        },
      }
    );

  const handleSwitchHorizontalAndZoom = () => {
    switchScrollMode(ScrollMode.Horizontal);
    zoomTo(SpecialZoomLevel.PageFit);
  };
  const handleSwitchVerticalAndZoom = () => {
    switchScrollMode(ScrollMode.Vertical);
    zoomTo(SpecialZoomLevel.PageWidth);
  };
  const handleSwitchWrappedAndZoom = () => {
    switchScrollMode(ScrollMode.Wrapped);
    zoomTo(SpecialZoomLevel.PageFit);
  };

  const renderToolbarForTabletAndDesktop = (
    Toolbar: (props: ToolbarProps) => React.ReactElement
  ) => (
    <>
      <Toolbar>
        {(slots: ToolbarSlot) => {
          const {
            Zoom,
            Print,
            ZoomIn,
            ZoomOut,
            Download,
            GoToNextPage,
            GoToLastPage,
            GoToFirstPage,
            NumberOfPages,
            EnterFullScreen,
            CurrentPageInput,
            GoToPreviousPage,
            ShowSearchPopover,
          } = slots;
          return (
            <div
              style={{
                alignItems: "center",
                display: "flex",
                width: "100%",
              }}
            >
              <div style={{ padding: "0px 2px" }}>
                <ShowSearchPopover />
              </div>
              <div style={{ padding: "0px 2px" }}>
                <ZoomOut />
              </div>
              <div style={{ padding: "0px 2px" }}>
                <Zoom />
              </div>
              <div style={{ padding: "0px 2px" }}>
                <ZoomIn />
              </div>
              <div
                style={{
                  padding: "0px 2px",
                  marginLeft: "auto",
                  display: "flex",
                }}
              >
                <Button
                  sx={{ ...jumpBackAndForwardButtonStyle, minWidth: "unset" }}
                  onClick={handleJumpBackToPage}
                >
                  <SvgIcon component={ArrowLeft} />
                </Button>
                <GoToPreviousPage />
              </div>
              <div style={{ padding: "0px 2px", width: "4rem" }}>
                <CurrentPageInput />
              </div>
              <div
                style={{ padding: "0px 2px", width: "4rem", marginLeft: "5px" }}
              >
                / <NumberOfPages />
              </div>
              <div style={{ padding: "0px 2px" }}>
                <GoToNextPage />
              </div>
              <div style={{ padding: "0px 2px" }}>
                <Button
                  sx={{ ...jumpBackAndForwardButtonStyle, minWidth: "unset" }}
                  onClick={handleJumpForwardToPage}
                >
                  <SvgIcon component={ArrowRight} />
                </Button>
              </div>

              <div
                style={{
                  padding: "0px 2px",
                  marginRight: 0,
                  display: "flex",
                  marginLeft: "auto",
                }}
              >
                <GoToFirstPage />
                <GoToLastPage />
              </div>
              {!isTablet && (
                <>
                  <div style={{ padding: "0px 2px" }}>
                    <button
                      onClick={handleSwitchHorizontalAndZoom}
                      style={{ border: "none" }}
                    >
                      <HorizontalScrollingIcon />
                    </button>
                    <button
                      onClick={handleSwitchVerticalAndZoom}
                      style={{ border: "none" }}
                    >
                      <VerticalScrollingIcon />
                    </button>
                    <button
                      onClick={handleSwitchWrappedAndZoom}
                      style={{ border: "none" }}
                    >
                      <WrappedScrollingIcon />
                    </button>
                  </div>
                  <div style={{ padding: "0px 2px" }}>
                    <EnterFullScreen />
                  </div>
                  <div style={{ padding: "0px 2px" }}>
                    <Download />
                  </div>
                  <div style={{ padding: "0px 2px" }}>
                    <Print />
                  </div>
                </>
              )}

              {isTablet && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    aria-haspopup="true"
                    sx={{ padding: "4px" }}
                    onClick={handleMobileMenuOpen}
                    aria-expanded={isMobileMenuOpen ? "true" : undefined}
                    aria-controls={
                      isMobileMenuOpen ? "account-menu" : undefined
                    }
                  >
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </IconButton>
                  <Menu
                    anchorEl={mobileMenuAnchorEl}
                    id="account-menu"
                    open={isMobileMenuOpen}
                    onClose={handleMobileMenuClose}
                    onClick={handleMobileMenuClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        overflow: "visible",
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                        mt: 1.5,
                        "& .MuiAvatar-root": {
                          width: 32,
                          height: 32,
                          ml: 0,
                          mr: 0,
                        },
                        "&:before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: "background.paper",
                          transform: "translateY(-50%) rotate(45deg)",
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <MenuItem>
                      <button
                        onClick={handleSwitchHorizontalAndZoom}
                        style={{ border: "none", background: "none" }}
                      >
                        <HorizontalScrollingIcon />
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        onClick={handleSwitchVerticalAndZoom}
                        style={{ border: "none", background: "none" }}
                      >
                        <VerticalScrollingIcon />
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <button
                        onClick={handleSwitchWrappedAndZoom}
                        style={{ border: "none", background: "none" }}
                      >
                        <WrappedScrollingIcon />
                      </button>
                    </MenuItem>
                    <MenuItem>
                      <EnterFullScreen />
                    </MenuItem>
                    <MenuItem>
                      <Download />
                    </MenuItem>
                    <MenuItem>
                      <Print />
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </div>
          );
        }}
      </Toolbar>
      <Button color="error" sx={closeButtonStyle} onClick={handleOnClose}>
        {translate(RESOURCES_KEYS.CLOSE_VIEWER_MODAL)}
      </Button>
    </>
  );

  const renderToolbarForMobile = (
    Toolbar: (props: ToolbarProps) => React.ReactElement
  ) => (
    <>
      <Toolbar>
        {(slots: ToolbarSlot) => {
          const {
            Zoom,
            Print,
            ZoomIn,
            ZoomOut,
            Download,
            GoToNextPage,
            GoToLastPage,
            GoToFirstPage,
            NumberOfPages,
            EnterFullScreen,
            CurrentPageInput,
            GoToPreviousPage,
            ShowSearchPopover,
          } = slots;
          return (
            <div
              style={{
                alignItems: "center",
                display: "flex",
                width: "100%",
                flexWrap: isMobile ? "nowrap" : "wrap",
                justifyContent: isMobile ? "center" : "space-between",
                padding: isMobile ? "8px 0" : "0",
                position: "relative",
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: isMobile ? "16px" : "0",
                }}
              >
                <ShowSearchPopover />
                <Button
                  sx={{
                    ...jumpBackAndForwardButtonStyle,
                    padding: "0",
                    minWidth: "25px",
                  }}
                  onClick={handleJumpBackToPage}
                >
                  <SvgIcon component={ArrowLeft} />
                </Button>
                <GoToPreviousPage />
                <div style={{ padding: "0px 2px", width: "3rem" }}>
                  <CurrentPageInput />
                </div>
                <div
                  style={{
                    padding: "0px 2px",
                    width: "3rem",
                    marginLeft: "2px",
                  }}
                >
                  / <NumberOfPages />
                </div>
                <GoToNextPage />
                <Button
                  sx={{
                    ...jumpBackAndForwardButtonStyle,
                    padding: "0",
                    minWidth: "25px",
                  }}
                  onClick={handleJumpForwardToPage}
                >
                  <SvgIcon component={ArrowRight} />
                </Button>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  size="small"
                  aria-haspopup="true"
                  sx={{ padding: "4px" }}
                  onClick={handleMobileMenuOpen}
                  aria-expanded={isMobileMenuOpen ? "true" : undefined}
                  aria-controls={isMobileMenuOpen ? "account-menu" : undefined}
                >
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchorEl}
                  id="account-menu"
                  open={isMobileMenuOpen}
                  onClose={handleMobileMenuClose}
                  onClick={handleMobileMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: "visible",
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      mt: 1.5,
                      "& .MuiAvatar-root": {
                        width: 32,
                        height: 32,
                        ml: 0,
                        mr: 0,
                      },
                      "&:before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  <MenuItem>
                    <div style={{ padding: "0px 2px" }}>
                      <ZoomOut />
                    </div>
                    <div style={{ padding: "0px 2px" }}>
                      <Zoom />
                    </div>
                    <div style={{ padding: "0px 2px" }}>
                      <ZoomIn />
                    </div>
                  </MenuItem>
                  <MenuItem>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <GoToFirstPage />
                      <GoToLastPage />
                    </div>
                  </MenuItem>
                  <MenuItem>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={handleSwitchHorizontalAndZoom}
                        style={{ border: "none", background: "none" }}
                      >
                        <HorizontalScrollingIcon />
                      </button>
                      <button
                        onClick={handleSwitchVerticalAndZoom}
                        style={{ border: "none", background: "none" }}
                      >
                        <VerticalScrollingIcon />
                      </button>
                      <button
                        onClick={handleSwitchWrappedAndZoom}
                        style={{ border: "none", background: "none" }}
                      >
                        <WrappedScrollingIcon />
                      </button>
                    </div>
                  </MenuItem>
                  <MenuItem>
                    <EnterFullScreen />
                    <span style={{ marginLeft: "8px" }}>Full Screen</span>
                  </MenuItem>
                  <MenuItem>
                    <Download />
                    <span style={{ marginLeft: "8px" }}>Download</span>
                  </MenuItem>
                  <MenuItem>
                    <Print />
                    <span style={{ marginLeft: "8px" }}>Print</span>
                  </MenuItem>
                </Menu>
              </div>
            </div>
          );
        }}
      </Toolbar>
      <Button color="error" sx={closeButtonStyle} onClick={handleOnClose}>
        {translate(RESOURCES_KEYS.CLOSE_VIEWER_MODAL)}
      </Button>
    </>
  );

  const defaultLayoutPluginInstance =
    process.env.REACT_APP_PDF_FEATURE_ENABLED === "true"
      ? defaultLayoutPlugin({
          renderToolbar: isMobile
            ? renderToolbarForMobile
            : renderToolbarForTabletAndDesktop,
          sidebarTabs: (defaultTabs) =>
            defaultTabs.concat({
              content: sidebarNotes,
              icon: <MessageIcon />,
              title: "Notes",
            }),
        })
      : defaultLayoutPlugin({
          renderToolbar: isMobile
            ? renderToolbarForMobile
            : renderToolbarForTabletAndDesktop,
        });

  const {
    data: updatePdfDocumentReadDateData,
    mutate: updateDocumentReadDate,
    isError: isUpdatePdfDocumentReadDateDataInError,
  } = useMutation(() =>
    updatePdfDocumentReadDateAsync({
      parentDocumentId: parentDocumentId,
      fileId: documentVersion.file.id,
      documentVersionId: documentVersion.id,
    })
  );

  const { mutate: mutateGetVersionsData } = useMutation(
    ({ username }: { username: string }) => getVersionsAsync(username)
  );

  const handleOnDocumentLoad = (e: DocumentLoadEvent) => {
    if (isOnline) {
      updateDocumentReadDate();
      if (process.env.REACT_APP_PDF_FEATURE_ENABLED === "true") {
        setTimeout(() => {
          mutateGetVersionsData(
            { username: accounts?.[0].username },
            {
              onSuccess: ({ responseData }) => {
                let unreadDocs = 0;
                responseData?.items.forEach((item: any) => {
                  item.applicables.forEach((applicable: any) => {
                    if (applicable.uds.lastRead === null) {
                      unreadDocs++;
                    }
                  });
                  item.nextApplicables.forEach((nextApplicable: any) => {
                    if (nextApplicable.uds.lastRead === null) {
                      unreadDocs++;
                    }
                  });
                });

                if ("setAppBadge" in navigator) {
                  (navigator as any).setAppBadge(unreadDocs);
                }
              },
              onError: () =>
                console.error(`Le chargement des versions a échoué`),
            }
          );
        }, 1000);

        fetchWrapper
          .get(
            `${process.env.REACT_APP_BACK_END_API}/notes?userId=${localAccountId}&documentId=${documentVersion.parentDocument.id}`
          )
          .then((response) => response.json())
          .then((data) => {
            var result: Note[] = data.map((note: NoteResponse) => ({
              id: note.id,
              noteContent: note.noteContent,
              quotePosition: JSON.parse(note.quotePosition),
              quote: note.quote,
              editing: false,
            }));
            setNotes(result);
          })
          .catch(() => console.error(`Le chargement des notes a échoué`));
      }
    }
  };

  const parentDocumentId = documentVersion.parentDocument.id;
  const foundDocumentStatus =
    documentActionsState.documentActionsStatus.get(parentDocumentId);

  const handleJumpBackToPage = useCallback(() => {
    history.back();
  }, []);

  const handleJumpForwardToPage = useCallback(() => {
    history.forward();
  }, []);

  const handleOnClose = useCallback(() => {
    if (process.env.REACT_APP_PDF_FEATURE_ENABLED === "true" && isOnline) {
      const url = `${process.env.REACT_APP_BACK_END_API}/statistics?userId=${localAccountId}&documentVersionId=${documentVersion.id}`;
      const statistic: Statistic = {
        lastReadPage: currentPage,
        lastReadProgression: 0,
      };
      fetchWrapper.post(url, statistic);
    }

    if (
      Boolean(foundDocumentStatus?.isUnread) &&
      updatePdfDocumentReadDateData?.isSuccess
    ) {
      dispatch({ type: "toggleIsRead", parentDocumentId: parentDocumentId });
    }

    onClose();

    navigate(pathname, { replace: true });
  }, [
    isOnline,
    pathname,
    currentPage,
    localAccountId,
    parentDocumentId,
    documentVersion.id,
    foundDocumentStatus?.isUnread,
    updatePdfDocumentReadDateData?.isSuccess,
    onClose,
    navigate,
    dispatch,
  ]);

  useEffect(() => {
    if (isUpdatePdfDocumentReadDateDataInError) {
      setOpenDialogAlert(true);
    }
  }, [isUpdatePdfDocumentReadDateDataInError]);

  const isAckRequired = foundDocumentStatus?.isAckRequired !== false;

  const pdfData = useMemo(
    () => ({ data: new Uint8Array(pdfFileBuffer) }),
    [pdfFileBuffer]
  );

  const [noteMessage, setNoteMessage] = useState("");
  const [pageNoteMessage, setPageNoteMessage] = useState("");
  const [editNote, setEditNote] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);
  const notesContainerRef = useRef<HTMLDivElement | null>(null);

  const noteElements: Map<Guid, HTMLElement> = useMemo(() => new Map(), []);

  const renderHighlightTarget = (props: RenderHighlightTargetProps) => (
    <div
      style={{
        background: "#eee",
        display: "flex",
        position: "absolute",
        left: `${props.selectionRegion.left}%`,
        top: `${props.selectionRegion.top + props.selectionRegion.height}%`,
        transform: "translate(0, 8px)",
        zIndex: 1,
      }}
    >
      <Tooltip
        position={Position.TopCenter}
        target={
          <Button onClick={props.toggle}>
            <MessageIcon />
          </Button>
        }
        content={() => (
          <div style={{ width: "100px" }}>
            {translate(RESOURCES_KEYS.PDF_ADD_A_NOTE)}
          </div>
        )}
        offset={{ left: 0, top: -8 }}
      />
    </div>
  );

  const renderHighlightContent = (props: RenderHighlightContentProps) => {
    const addNote = () => {
      if (noteMessage !== "" && isOnline) {
        props.cancel();
        const url = `${process.env.REACT_APP_BACK_END_API}/notes?userId=${localAccountId}&documentVersionId=${documentVersion.id}`;
        const newNote: NoteRequest = {
          quote: props.selectedText,
          quotePosition: JSON.stringify(props.highlightAreas),
          noteContent: noteMessage,
        };

        fetchWrapper.post(url, newNote).then((response) => {
          constructResponseData<Guid>(
            response,
            `L'ajout de la note a échoué`
          ).then((result) => {
            if (result.responseData) {
              console.log(result.responseData);
              const note: Note = {
                id: result.responseData,
                noteContent: noteMessage,
                quotePosition: props.highlightAreas,
                quote: props.selectedText,
                editing: false,
              };
              setNotes(notes.concat([note]));
              setNoteMessage("");
            }
          });
        });
      }
    };

    const cancelNote = () => {
      props.cancel();
      setNoteMessage("");
    };

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid rgba(0, 0, 0, .3)",
          borderRadius: "2px",
          padding: "8px",
          position: "absolute",
          left: `${props.selectionRegion.left}%`,
          top: `${props.selectionRegion.top + props.selectionRegion.height}%`,
          zIndex: 1,
        }}
      >
        <div>
          <textarea
            rows={3}
            style={{ border: "1px solid rgba(0, 0, 0, .3)" }}
            onChange={(e) => setNoteMessage(e.currentTarget.value)}
            value={noteMessage}
          />
        </div>
        <div
          style={{
            display: "flex",
            marginTop: "8px",
          }}
        >
          <div style={{ marginRight: "8px" }}>
            <PrimaryButton onClick={addNote}>
              {translate(RESOURCES_KEYS.ADD)}
            </PrimaryButton>
          </div>
          <Button onClick={cancelNote}>
            {translate(RESOURCES_KEYS.CANCEL)}
          </Button>
        </div>
      </div>
    );
  };

  const jumpToNote = (note: Note) => {
    activateTab(3);
    const notesContainer = notesContainerRef.current;
    if (noteElements.has(note.id) && notesContainer) {
      const noteElement = noteElements.get(note.id);
      if (noteElement) {
        notesContainer.scrollTop = noteElement.getBoundingClientRect().top;
      }
    }
  };

  const renderHighlights = (props: RenderHighlightsProps) => (
    <div>
      {notes.map((note, key) => (
        <Fragment key={key}>
          {note.quotePosition
            .filter((area) => area.pageIndex === props.pageIndex)
            .map((area, idx) => (
              <div
                key={idx}
                style={Object.assign(
                  {},
                  {
                    background: "yellow",
                    opacity: 0.4,
                  },
                  props.getCssProperties(area, props.rotation)
                )}
                onClick={() => jumpToNote(note)}
              />
            ))}
        </Fragment>
      ))}
    </div>
  );

  const highlightPluginInstance = highlightPlugin({
    renderHighlightTarget,
    renderHighlightContent,
    renderHighlights,
  });

  const { jumpToHighlightArea } = highlightPluginInstance;

  useEffect(() => {
    return () => {
      noteElements.clear();
    };
  }, [noteElements]);

  const addNewNote = () => {
    if (pageNoteMessage !== "" && isOnline) {
      const url = `${process.env.REACT_APP_BACK_END_API}/notes?userId=${localAccountId}&documentVersionId=${documentVersion.id}`;
      const quote = "";
      const quotePosition = [
        {
          height: 0,
          left: 0,
          pageIndex: currentPage,
          top: 0,
          width: 0,
        },
      ];
      const newNote: NoteRequest = {
        quote: quote,
        quotePosition: JSON.stringify(quotePosition),
        noteContent: pageNoteMessage,
      };
      fetchWrapper.post(url, newNote).then((response) => {
        constructResponseData<Guid>(
          response,
          `L'ajout de la note a échoué`
        ).then((result) => {
          if (result.responseData) {
            console.log(result.responseData);
            const note: Note = {
              id: result.responseData,
              noteContent: pageNoteMessage,
              quotePosition: quotePosition,
              quote: quote,
              editing: false,
            };
            setNotes(notes.concat([note]));
            setPageNoteMessage("");
          }
        });
      });
    }
  };

  const cancelNewNote = () => {
    setPageNoteMessage("");
  };

  const EditNote = (noteId: Guid) => {
    const nextNotes = [...notes];
    const note: Note | undefined = nextNotes.find((a) => a.id === noteId);
    if (note) {
      note.editing = true;
      setNotes(nextNotes);
      setEditNote(note.noteContent);
    }
  };

  const CancelEditingNote = (noteId: Guid) => {
    const nextNotes = [...notes];
    const note: Note | undefined = nextNotes.find((a) => a.id === noteId);
    if (note) {
      note.editing = false;
      setNotes(nextNotes);
    }
  };

  const UpdateNote = (noteId: Guid) => {
    const nextNotes = [...notes];
    const note: Note | undefined = nextNotes.find((a) => a.id === noteId);
    if (note) {
      note.editing = false;
      note.noteContent = editNote;
      setNotes(nextNotes);
      setEditNote("");
      fetchWrapper
        .put(
          `${
            process.env.REACT_APP_BACK_END_API
          }/notes/${noteId.toString()}/content`,
          {
            noteContent: editNote,
          }
        )
        .then((response) => {
          constructResponseData<boolean>(
            response,
            `La mise à jour de la note a échouée`
          );
        });
    }
  };

  const RemoveNote = async (noteId: Guid) => {
    await fetchWrapper
      .delete(
        `${process.env.REACT_APP_BACK_END_API}/notes/${noteId.toString()}`
      )
      .then(() => {
        setNotes(notes.filter((note) => note.id !== noteId));
      })
      .catch(() => console.log(`La suppression de la note a échouée`));
  };

  const compareFn = (a: Note, b: Note) => {
    const aPageIndex = a.quotePosition[0].pageIndex;
    const bPageIndex = b.quotePosition[0].pageIndex;

    if (aPageIndex > bPageIndex) {
      return 1;
    } else if (aPageIndex < bPageIndex) {
      return -1;
    } else {
      const aTop = a.quotePosition[0].top;
      const bTop = b.quotePosition[0].top;
      if (aTop > bTop) {
        return 1;
      } else if (aTop < bTop) {
        return -1;
      } else {
        const aLeft = a.quotePosition[0].left;
        const bLeft = b.quotePosition[0].left;
        if (aLeft > bLeft) {
          return 1;
        } else if (aLeft < bLeft) {
          return -1;
        } else {
          return 0;
        }
      }
    }
  };

  const sidebarNotes = (
    <div ref={notesContainerRef}>
      {
        <div>
          <h5 style={{ backgroundColor: "rgb(238,238,238)" }}>
            {translate(RESOURCES_KEYS.PDF_ADD_A_PAGE_NOTE)}
          </h5>
          <blockquote
            style={{
              borderLeft: "2px solid rgb(238, 238, 238)",
              fontSize: "11px",
            }}
          >
            <i> {translate(RESOURCES_KEYS.PDF_ADD_A_PAGE_NOTE_DESCRIPTION)}</i>
          </blockquote>
          <textarea
            style={{
              margin: "0 8px",
              width: "calc(100% - 20px)",
              border: "1px solid rgb(238, 238, 238)",
            }}
            rows={5}
            value={pageNoteMessage}
            onChange={(e) => setPageNoteMessage(e.target.value)}
          />
          <div style={{ textAlign: "right" }}>
            <Button
              variant="outlined"
              size="small"
              color="success"
              startIcon={
                <FontAwesomeIcon
                  icon={faPlus}
                  size="2xs"
                  color="rgb(46, 125, 50)"
                />
              }
              onClick={addNewNote}
            >
              {translate(RESOURCES_KEYS.ADD)}
            </Button>
            &nbsp;
            <Button
              variant="outlined"
              size="small"
              color="error"
              endIcon={
                <FontAwesomeIcon
                  icon={faXmark}
                  size="2xs"
                  color="rgb(249, 0, 20)"
                />
              }
              onClick={cancelNewNote}
            >
              {translate(RESOURCES_KEYS.CANCEL)}
            </Button>
          </div>
          <hr />
        </div>
      }

      <h5
        style={{
          margin: "2px",
          paddingLeft: "8px",
          backgroundColor: "rgb(238,238,238)",
        }}
      >
        {notes.length !== 0
          ? translate(RESOURCES_KEYS.PDF_NOTE_TITLE)
          : translate(RESOURCES_KEYS.PDF_NO_NOTE)}
      </h5>

      <div
        style={{
          height: "calc(100% - 270px)",
          overflow: "auto",
        }}
      >
        {notes.sort(compareFn).map((note, key) => {
          return (
            <div
              key={key}
              style={{
                borderBottom: "1px solid rgba(0, 0, 0, .3)",
                cursor: "pointer",
                padding: "8px",
                backgroundColor: note.editing
                  ? "rgb(255, 255, 155)"
                  : "rgba(0, 0, 0, 0)",
              }}
              onClick={() => jumpToHighlightArea(note.quotePosition[0])}
              ref={(ref): void => {
                noteElements.set(note.id, ref as HTMLElement);
              }}
            >
              <div
                style={{
                  float: "right",
                  border: "1px solid rgb(238, 238, 238)",
                  padding: "0 2px",
                  margin: "-8px -7px 0 0",
                  fontWeight: "bold",
                  fontSize: "11px",
                }}
              >
                page {note.quotePosition[0].pageIndex + 1}
              </div>
              <blockquote
                style={{
                  borderLeft: "2px solid rgb(238, 238, 238)",
                  fontSize: "12px",
                  lineHeight: 1.5,
                  margin: "11px 0 8px 0",
                  padding: "0px 8px 8px 8px",
                  textAlign: "justify",
                }}
              >
                {note.quote}
              </blockquote>
              {note.editing ? (
                <div>
                  <textarea
                    style={{
                      margin: "0 3px",
                      width: "calc(100% - 5px)",
                      border: "1px solid rgb(238, 238, 238)",
                    }}
                    rows={8}
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                  ></textarea>
                  <div style={{ textAlign: "right" }}>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={
                        <FontAwesomeIcon
                          icon={faCheck}
                          size="2xs"
                          color="rgb(0, 73, 152)"
                        />
                      }
                      onClick={() => UpdateNote(note.id)}
                    ></Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={
                        <FontAwesomeIcon
                          icon={faBan}
                          rotate={90}
                          size="2xs"
                          color="rgb(249, 0, 20)"
                        />
                      }
                      onClick={() => CancelEditingNote(note.id)}
                    ></Button>
                  </div>
                </div>
              ) : (
                <div>
                  <span>{note.noteContent}</span>
                  <div style={{ textAlign: "right" }}>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          size="2xs"
                          color="rgba(0, 73, 152, 0.5)"
                        />
                      }
                      onClick={() => EditNote(note.id)}
                    ></Button>
                    {/* AJOUTER UNE MODAL DE CONFIRMATION POUR LA SUPPRESSION */}
                    <Button
                      size="small"
                      color="error"
                      startIcon={
                        <FontAwesomeIcon
                          icon={faTrashCan}
                          size="2xs"
                          color="rgba(249, 0, 20, 0.5)"
                        />
                      }
                      onClick={() => RemoveNote(note.id)}
                    ></Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const { activateTab } = defaultLayoutPluginInstance;

  interface Update {
    /**
     * The action that triggered the change.
     */
    action: Action;
    /**
     * The new location.
     */
    location: any;
  }

  const handlePageChange = ({
    currentPage: newCurrentPage,
  }: PageChangeEvent) => {
    setCurrentPage(newCurrentPage);

    if (
      currentPage !== newCurrentPage &&
      search !== `?page=${newCurrentPage + 1}`
    ) {
      navigate(`?page=${newCurrentPage + 1}`, {
        state: { currentPageIndex: newCurrentPage },
      });
    }
  };

  useEffect(() => {
    const handleLocationChange = ({ location: { state }, action }: Update) => {
      const currentPageIndex: number = state?.currentPageIndex;
      const newSearchValue = state?.search;
      if (action === "POP" && currentPageIndex !== undefined) {
        jumpToPage(currentPageIndex);
      } else if (action === "POP" && !newSearchValue) {
        handleOnClose();
      }
    };

    const unlisten = history.listen(handleLocationChange);

    return () => unlisten();
  }, [handleOnClose, jumpToPage, pathname, search]);

  if (isGetDocumentStatisticsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Modal
      open={isViewerOpen}
      onClose={handleOnClose}
      aria-labelledby="pdf-viewer"
      aria-describedby="pdf-viewer"
    >
      <>
        <Box sx={boxSyle}>
          <Worker workerUrl={PDFJSWorker}>
            {pdfData.data && (
              <Viewer
                fileUrl={pdfData.data}
                onPageChange={handlePageChange}
                enableSmoothScroll={false}
                initialPage={currentPage}
                scrollMode={ScrollMode.Vertical}
                plugins={
                  process.env.REACT_APP_PDF_FEATURE_ENABLED === "true"
                    ? [
                        zoomPluginInstance,
                        highlightPluginInstance,
                        scrollModePluginInstance,
                        defaultLayoutPluginInstance,
                        pageNavigationPluginInstance,
                      ]
                    : [
                        zoomPluginInstance,
                        scrollModePluginInstance,
                        defaultLayoutPluginInstance,
                        pageNavigationPluginInstance,
                      ]
                }
                localization={
                  (i18n.language === LANGUAGES.fr_FR
                    ? fr_FR
                    : en_US) as unknown as LocalizationMap
                }
                onDocumentLoad={handleOnDocumentLoad}
              />
            )}
          </Worker>
          {navigator.onLine && isAckRequired && (
            <AcknowledgmentConfirmation documentVersion={documentVersion} />
          )}
        </Box>
        {isOpenDialogAlert && (
          <DialogAlertMessage
            isOpenDialogAlert={isOpenDialogAlert}
            onClose={() => setOpenDialogAlert(false)}
            apiErrorTypeOrDetail={computeErrorMessage(
              updatePdfDocumentReadDateData?.error
            )}
          />
        )}
      </>
    </Modal>
  );
};

export default PdfViewerModal;
